<template>
  <div>
    <dx-grid
      :url="url"
      :columns="columns"
      :actions="actions"
      :title="title"
      :beforeSetupGrid="beforeSetupGrid"
      :onLoaded="onGridResultsLoaded"
      ref="grid"
    ></dx-grid>
    <v-dialog persistent v-model="exportDialog" max-width="480">
      <v-card class="export-dialog-card">
        <div class="export-dialog-header">
          <v-icon large color="white" class="mr-3"
            >mdi-file-export-outline</v-icon
          >
          <span class="export-dialog-title">Export Geo Student Stops</span>
        </div>

        <v-card-text class="pt-4 pb-2 px-4">
          <div
            v-if="!exportRunning && !exportCancelled && !exportDone"
            class="export-idle-state"
          >
            <div class="export-idle-icon-wrap mb-3">
              <v-icon color="success" size="46">mdi-file-export</v-icon>
            </div>
            <div class="export-idle-title mb-2">Ready to export</div>
            <div class="export-idle-subtitle grey--text mb-4">
              {{ exportTotal.toLocaleString() }} records will be fetched in
              batches of 10,000 based on your current filters.
            </div>
            <button class="export-action-btn" @click="startExport()">
              <span class="export-action-btn-inner">
                <v-icon color="white" style="font-size:18px; margin-right:7px;"
                  >mdi-play-circle-outline</v-icon
                >
                Start Export
              </span>
            </button>
          </div>

          <div v-else-if="exportRunning">
            <div class="export-status-row mb-4">
              <v-icon color="success" class="pulse-icon mr-2"
                >mdi-cloud-download-outline</v-icon
              >
              <span class="export-status-text"
                >Fetching records from server...</span
              >
            </div>

            <div class="export-progress-wrap mb-1">
              <v-progress-linear
                :value="exportPercent"
                color="success"
                background-color="#e8f5e9"
                height="10"
                rounded
              ></v-progress-linear>
              <v-progress-linear
                indeterminate
                color="light-green lighten-2"
                height="4"
                class="export-shimmer-bar"
              ></v-progress-linear>
            </div>

            <div class="export-counts-row mt-3">
              <span class="export-count-fetched">{{
                exportFetched.toLocaleString()
              }}</span>
              <span class="export-count-sep"> / </span>
              <span class="export-count-total"
                >{{ exportTotal.toLocaleString() }} records</span
              >
              <v-spacer></v-spacer>
              <span class="export-percent-badge">{{ exportPercent }}%</span>
            </div>

            <div class="export-chunk-info mt-2">
              <v-icon
                small
                color="light-green darken-1"
                class="mr-1 export-spin-icon"
                style="vertical-align:middle"
                >mdi-autorenew</v-icon
              >
              <span class="export-batch-loading-text">
                Loading batch {{ exportCurrentChunk }} of
                {{ exportTotalChunks }}...
              </span>
            </div>
          </div>

          <div v-else-if="exportCancelled" class="export-cancelled-state">
            <v-icon color="grey lighten-1" size="48" class="mb-3"
              >mdi-cancel</v-icon
            >
            <div class="export-final-text grey--text">Export cancelled.</div>
          </div>

          <div v-else class="export-done-state">
            <div class="export-done-check-wrap mb-3">
              <v-icon color="success" size="52">mdi-check-circle</v-icon>
            </div>
            <div class="export-final-text success--text mb-1">
              {{ exportFetched.toLocaleString() }} records ready!
            </div>
            <div class="export-done-subtitle grey--text mb-5">
              Click the button below to download your CSV file.
            </div>
            <button class="export-action-btn" @click="triggerDownload()">
              <span class="export-action-btn-inner">
                <v-icon color="white" style="font-size:18px; margin-right:7px;"
                  >mdi-download</v-icon
                >
                Download CSV
              </span>
            </button>
          </div>
        </v-card-text>

        <v-card-actions class="px-3 pb-4 pt-2">
          <v-spacer></v-spacer>
          <v-btn
            v-if="exportRunning"
            outline
            color="grey darken-1"
            @click="cancelExport()"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn
            v-else
            outline
            color="grey darken-1"
            @click="closeExportDialog()"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="reportDialog" max-width="520">
      <v-card class="report-dialog-card">
        <div class="report-dialog-header">
          <v-icon large color="white" class="mr-3">mdi-file-pdf-box</v-icon>
          <div>
            <div class="report-dialog-title">Bus Schedule Report</div>
            <div class="report-dialog-subtitle">
              Configure and download your PDF report
            </div>
          </div>
        </div>

        <v-card-text class="pt-4 pb-2 px-4 report-card-body">
          <!-- Schedule -->
          <div class="report-section-label mb-2">
            <v-icon small color="green darken-2" class="mr-1"
              >mdi-calendar-clock</v-icon
            >
            Schedule
          </div>
          <v-autocomplete
            v-model="reportSchedule"
            label="Schedule *"
            :items="scheduleOptions"
            :rules="[rules.required]"
            ref="reportSchedule"
            outline
            dense
            clearable
            class="mb-2"
          ></v-autocomplete>

          <!-- Filters — only shown once a schedule is selected -->
          <div v-if="reportSchedule">
            <v-divider class="mb-3"></v-divider>

            <div class="report-section-label mb-1">
              <v-icon small color="green darken-2" class="mr-1"
                >mdi-filter-outline</v-icon
              >
              Filters
              <span class="report-optional-badge">optional</span>
            </div>
            <div class="report-filter-hint mb-3">
              <v-icon
                small
                color="grey darken-1"
                class="mr-1"
                style="margin-top:1px;flex-shrink:0"
                >mdi-information-outline</v-icon
              >
              Only students matching the selected filters will appear. Buses
              with no matching students are excluded from the report.
            </div>

            <!-- Location row -->
            <v-layout row wrap class="mb-0">
              <v-flex xs6 class="pr-2">
                <v-autocomplete
                  v-model="reportBuildings"
                  label="Building"
                  :items="reportBuildingOptions"
                  outline
                  dense
                  clearable
                  multiple
                >
                  <template slot="selection" slot-scope="{ index }">
                    <span v-if="index === 0" class="report-select-summary">
                      {{
                        selectionLabel(reportBuildings, reportBuildingOptions)
                      }}
                    </span>
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex xs6 class="pl-2" v-if="!reportUseDayCampClasses">
                <v-autocomplete
                  v-model="reportBranches"
                  label="Branch"
                  :items="reportBranchOptions"
                  outline
                  dense
                  clearable
                  multiple
                >
                  <template slot="selection" slot-scope="{ index }">
                    <span v-if="index === 0" class="report-select-summary">
                      {{ selectionLabel(reportBranches, reportBranchOptions) }}
                    </span>
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>

            <!-- Class info row -->
            <v-layout row wrap>
              <v-flex xs6 class="pr-2">
                <v-autocomplete
                  v-model="reportClasses"
                  label="Grades"
                  :items="reportClassOptions"
                  outline
                  dense
                  clearable
                  multiple
                >
                  <template slot="selection" slot-scope="{ index }">
                    <span v-if="index === 0" class="report-select-summary">
                      {{ selectionLabel(reportClasses, reportClassOptions) }}
                    </span>
                  </template>
                </v-autocomplete>
              </v-flex>
              <v-flex xs6 class="pl-2">
                <v-autocomplete
                  v-model="reportSections"
                  label="Sections"
                  :items="reportSectionOptions"
                  outline
                  dense
                  clearable
                  multiple
                >
                  <template slot="selection" slot-scope="{ index }">
                    <span v-if="index === 0" class="report-select-summary">
                      {{ selectionLabel(reportSections, reportSectionOptions) }}
                    </span>
                  </template>
                </v-autocomplete>
              </v-flex>
            </v-layout>
          </div>
        </v-card-text>

        <v-divider class="mx-4"></v-divider>

        <v-card-text class="pt-3 pb-1 px-4">
          <div class="report-section-label mb-1">
            <v-icon small color="green darken-2" class="mr-1">mdi-file-document-outline</v-icon>
            Paper Size
          </div>
          <v-radio-group v-model="reportPaperSize" row class="mt-2 mb-0" hide-details>
            <v-radio color="success" label="A4 (default)" value="A4"></v-radio>
            <v-radio color="success" label='8.5×14" Legal' value="Legal"></v-radio>
          </v-radio-group>
        </v-card-text>

        <v-divider class="mx-4"></v-divider>

        <v-card-text class="pt-3 pb-1 px-4">
          <div class="report-section-label mb-0">
            <v-icon small color="green darken-2" class="mr-1">mdi-school-outline</v-icon>
            Options
          </div>
          <v-checkbox
            v-model="reportUseDayCampClasses"
            color="success"
            hide-details
            class="mt-1 mb-2"
          >
            <template slot="label">
              <span style="font-size:13px;">Use Day Camp Classes</span>
            </template>
          </v-checkbox>
        </v-card-text>

        <v-card-actions class="px-1 pb-4 pt-2">
          <v-spacer></v-spacer>
          <v-btn color="default" dark @click="closeReportDialog()"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            :loading="downloadingReport"
            depressed
            class="ml-2"
            @click="downloadStudentStopsReport()"
          >
            <v-icon left small>mdi-download</v-icon>
            Download PDF
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  data: () => ({
    title: "Geo Student Stops",
    actions: [],
    url: "",
    selectedSchedule: "",
    selectedBranch: "",
    allScheduleOptions: [],
    scheduleOptions: [],
    scheduleAction: null,
    latestGridRows: null,
    scheduleFilterOptions: [],
    scheduleFilterOptionsDC: [],
    exportDialog: false,
    exportRunning: false,
    exportFetched: 0,
    exportTotal: 0,
    exportCurrentChunk: 0,
    exportTotalChunks: 0,
    exportCancelled: false,
    exportDone: false,
    exportBatchLoading: false,
    exportAbortController: null,
    exportReadyRecords: null,
    exportBaseUrl: "",
    exportToken: "",
    reportDialog: false,
    reportSchedule: "",
    reportBuildings: [],
    reportBranches: [],
    reportClasses: [],
    reportSections: [],
    reportPaperSize: "A4",
    reportUseDayCampClasses: false,
    downloadingReport: false,
    rules: {
      required: (value) => !!value || "Required.",
    },
    columns: [
      {
        label: "Student ID",
        field: "studentID",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Account ID",
        field: "familyID",
        visible: true,
        link: "Account View",
        format: "",
        template: "link-action-cell-template",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "English Name",
        field: "engName",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Last Name",
        field: "lastName",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Last Name H",
        field: "lastNameH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Hebrew Name",
        field: "hebName",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Father Name H",
        field: "fatherNameH",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Reg ID",
        field: "regID",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Building",
        field: "building",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Branch",
        field: "branch",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Class",
        field: "class",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Section",
        field: "section",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Father Name",
        field: "fatherName",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Email",
        field: "emailAddress",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Address",
        field: "address1",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Home Phone",
        field: "homePhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Cell Phone",
        field: "cellPhone",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "City",
        field: "city",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "State",
        field: "state",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Zip Code",
        field: "zipCode",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Bus Code",
        field: "busCode",
        visible: false,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Stop Schedule",
        field: "stopSchedule",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Stop #",
        field: "stopNumber",
        visible: true,
        type: "number",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Stop Address",
        field: "stopAddress",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Stop Bus",
        field: "stopBus",
        visible: true,
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "Stop Updated On",
        field: "stopUpdatedOn",
        visible: true,
        type: "date",
        format: "MM/dd/yyyy hh:mm:ss a",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
      {
        label: "DOB",
        field: "dob",
        visible: false,
        type: "date",
        format: "MM/dd/yyyy",
        filterValues: [],
        filterValue: null,
        selectedFilterOperation: null,
      },
    ],
  }),
  created() {
    this.setupAccountIdLink();
  },
  computed: {
    exportPercent() {
      if (!this.exportTotal) return 0;
      return Math.min(
        100,
        Math.round((this.exportFetched / this.exportTotal) * 100)
      );
    },
    currentReportScheduleData() {
      const source = this.reportUseDayCampClasses
        ? this.scheduleFilterOptionsDC
        : this.scheduleFilterOptions;
      if (!this.reportSchedule || !Array.isArray(source)) {
        return null;
      }
      return source.find((s) => s.schedule === this.reportSchedule) || null;
    },
    hasBuildingInCombinations() {
      const data = this.currentReportScheduleData;
      return (
        !!data &&
        Array.isArray(data.combinations) &&
        data.combinations.some((c) => !!c.building)
      );
    },
    reportBuildingOptions() {
      const data = this.currentReportScheduleData;
      if (!data) return [];
      if (this.hasBuildingInCombinations) {
        // Location team: buildings only narrow by branch, never by grades/sections
        let combos = data.combinations;
        if (this.reportBranches && this.reportBranches.length)
          combos = combos.filter((c) => this.reportBranches.includes(c.branch));
        return [...new Set(combos.map((c) => c.building).filter((v) => !!v))].sort();
      }
      return Array.isArray(data.buildings) ? data.buildings : [];
    },
    reportBranchOptions() {
      const data = this.currentReportScheduleData;
      if (!data || !Array.isArray(data.combinations)) return [];
      // Location team: branches only narrow by building, never by grades/sections
      let combos = data.combinations;
      if (this.hasBuildingInCombinations && this.reportBuildings && this.reportBuildings.length)
        combos = combos.filter((c) => this.reportBuildings.includes(c.building));
      const all = [...new Set(combos.map((c) => c.branch).filter((v) => !!v))].sort();
      const userBranch = this.getSelectedBranch();
      if (userBranch) {
        const userBranches = userBranch
          .split(",")
          .map((b) => b.trim())
          .filter((b) => !!b);
        return all.filter((b) => userBranches.includes(b));
      }
      return all;
    },
    reportClassOptions() {
      const data = this.currentReportScheduleData;
      if (!data || !Array.isArray(data.combinations)) return [];
      // Academic team: grades filtered by location (building + branch), not by sections
      let combos = data.combinations;
      if (this.hasBuildingInCombinations && this.reportBuildings && this.reportBuildings.length)
        combos = combos.filter((c) => this.reportBuildings.includes(c.building));
      if (this.reportBranches && this.reportBranches.length)
        combos = combos.filter((c) => this.reportBranches.includes(c.branch));
      return [...new Set(combos.map((c) => c.class).filter((v) => !!v))].sort();
    },
    reportSectionOptions() {
      const data = this.currentReportScheduleData;
      if (!data || !Array.isArray(data.combinations)) return [];
      // Academic team: sections filtered by location + grades
      let combos = data.combinations;
      if (this.hasBuildingInCombinations && this.reportBuildings && this.reportBuildings.length)
        combos = combos.filter((c) => this.reportBuildings.includes(c.building));
      if (this.reportBranches && this.reportBranches.length)
        combos = combos.filter((c) => this.reportBranches.includes(c.branch));
      if (this.reportClasses && this.reportClasses.length)
        combos = combos.filter((c) => this.reportClasses.includes(c.class));
      return [...new Set(combos.map((c) => c.section).filter((v) => !!v))].sort();
    },
  },
  watch: {
    // Schedule: reset everything then auto-select all available options top-down.
    reportSchedule() {
      this.reportBuildings = [];
      this.reportBranches = [];
      this.reportClasses = [];
      this.reportSections = [];
      this.$nextTick(() => {
        this.reportBuildings = [...this.reportBuildingOptions];
        this.reportBranches = [...this.reportBranchOptions];
        this.$nextTick(() => {
          this.reportClasses = [...this.reportClassOptions];
          this.$nextTick(() => {
            this.reportSections = [...this.reportSectionOptions];
          });
        });
      });
    },
    // Location team: Building and Branch narrow each other's OPTIONS via computed,
    // but never auto-reset each other. Both cascade to re-select all Grades → Sections.
    reportBuildings() {
      this.$nextTick(() => {
        this.reportClasses = [...this.reportClassOptions];
        this.$nextTick(() => {
          this.reportSections = [...this.reportSectionOptions];
        });
      });
    },
    reportBranches() {
      this.$nextTick(() => {
        this.reportClasses = [...this.reportClassOptions];
        this.$nextTick(() => {
          this.reportSections = [...this.reportSectionOptions];
        });
      });
    },
    // Academic team: Grades cascade to re-select all Sections.
    reportClasses() {
      this.$nextTick(() => {
        this.reportSections = [...this.reportSectionOptions];
      });
    },
    // Day Camp toggle: reset all filters then re-run the auto-select cascade.
    reportUseDayCampClasses() {
      this.reportBuildings = [];
      this.reportBranches = [];
      this.reportClasses = [];
      this.reportSections = [];
      this.$nextTick(() => {
        this.reportBuildings = [...this.reportBuildingOptions];
        this.reportBranches = [...this.reportBranchOptions];
        this.$nextTick(() => {
          this.reportClasses = [...this.reportClassOptions];
          this.$nextTick(() => {
            this.reportSections = [...this.reportSectionOptions];
          });
        });
      });
    },
  },
  methods: {
    setupAccountIdLink() {
      const accountColumn = this.columns.find(
        (column) => column.field == "familyID"
      );
      if (!accountColumn) return;

      accountColumn.template = "link-action-cell-template";
      accountColumn.showLink = (row) =>
        !this.isBranchUser() && !!this.getRowAccountId(row);
      accountColumn.linkAction = (row) => {
        this.toAccount(row);
      };
    },
    beforeSetupGrid() {
      this.setupAccountIdLink();
      this.selectedBranch = this.getSelectedBranch();

      this.scheduleAction = {
        text: "Schedule",
        type: "select",
        values: this.scheduleOptions,
        value: this.selectedSchedule,
        clearable: true,
        width: "220px",
        onSelected: this.onScheduleSelected,
      };

      this.actions.push(this.scheduleAction);

      this.actions.push({
        text: "Export",
        name: "Export",
        action: this.exportData,
      });
      this.actions.push({
        text: "Bus Schedule Report",
        name: "Bus Schedule Report",
        action: this.openReportDialog,
      });
      this.url = this.buildGridUrl();

      // Load schedule options and filter metadata independently from the grid datasource.
      this.loadStopSchedules();
      this.loadScheduleFilterOptions();
      this.loadScheduleFilterOptions(true);
    },
    buildGridUrl() {
      return (
        "studentStop?schedule=" +
        encodeURIComponent(this.selectedSchedule || "") +
        "&branch=" +
        encodeURIComponent(this.selectedBranch || "")
      );
    },
    getSelectedBranch() {
      const authState =
        this.$store && this.$store.state
          ? this.$store.state.authentication
          : null;
      const branch = authState ? authState.branch : "";
      if (!branch) return "";

      const normalized = String(branch).trim();
      if (!normalized || normalized == "null" || normalized.includes("null")) {
        return "";
      }

      return normalized;
    },
    isBranchUser() {
      return !!this.getSelectedBranch();
    },
    onScheduleSelected(value) {
      const selectedValue = value || "";
      if (selectedValue == this.selectedSchedule) return;

      this.selectedSchedule = selectedValue;
      if (!selectedValue) {
        this.scheduleOptions.splice(
          0,
          this.scheduleOptions.length,
          ...this.allScheduleOptions
        );
      }
      this.syncScheduleAction();
      this.url = this.buildGridUrl();
      this.$refs.grid.reloadDataSource(this.url);
    },
    onGridResultsLoaded(result) {
      this.latestGridRows = this.getRowsFromGridResult(result);
      this.applyScheduleOptionsIntersection();
    },
    getRowsFromGridResult(result) {
      if (Array.isArray(result)) return result;
      if (result && Array.isArray(result.value)) return result.value;
      return null;
    },
    syncScheduleAction() {
      if (!this.scheduleAction) return;

      this.scheduleAction.values = this.scheduleOptions;
      this.scheduleAction.value = this.selectedSchedule;
    },
    applyScheduleOptionsIntersection() {
      if (
        !Array.isArray(this.allScheduleOptions) ||
        !this.allScheduleOptions.length
      ) {
        return;
      }

      if (!Array.isArray(this.latestGridRows) || !this.latestGridRows.length) {
        this.scheduleOptions.splice(
          0,
          this.scheduleOptions.length,
          ...this.allScheduleOptions
        );
        this.syncScheduleAction();
        return;
      }

      const gridScheduleMap = {};

      this.latestGridRows.forEach((row) => {
        if (!row) return;
        const scheduleValue = this.getScheduleValueFromRow(row);
        if (!scheduleValue) return;

        const schedule = String(scheduleValue).trim();
        if (!schedule) return;

        gridScheduleMap[schedule] = true;
      });

      const intersectedSchedules = this.allScheduleOptions.filter(
        (schedule) => !!gridScheduleMap[schedule]
      );

      // If intersection is empty, keep the UX stable by showing
      // the full source-of-truth list instead of an empty dropdown.
      if (!intersectedSchedules.length && !this.selectedSchedule) {
        this.scheduleOptions.splice(
          0,
          this.scheduleOptions.length,
          ...this.allScheduleOptions
        );
        this.syncScheduleAction();
        return;
      }

      if (
        this.selectedSchedule &&
        !intersectedSchedules.includes(this.selectedSchedule)
      ) {
        intersectedSchedules.unshift(this.selectedSchedule);
      }

      this.scheduleOptions.splice(
        0,
        this.scheduleOptions.length,
        ...intersectedSchedules
      );
      this.syncScheduleAction();
    },
    getScheduleValueFromRow(row) {
      if (!row) return "";
      return row.stopSchedule || "";
    },
    async loadStopSchedules() {
      try {
        const branch = this.getSelectedBranch();
        const url = branch
          ? "api/studentStop/GetStopSchedules?branch=" + encodeURIComponent(branch)
          : "api/studentStop/GetStopSchedules";
        const schedules = await this.$http.get(url);
        const values = Array.isArray(schedules) ? schedules : [];

        const normalizedValues = values
          .map((s) => (s === null || s === undefined ? "" : String(s).trim()))
          .filter((s) => !!s)
          .filter((value, index, arr) => arr.indexOf(value) === index)
          .sort((a, b) => b.localeCompare(a));

        this.allScheduleOptions.splice(
          0,
          this.allScheduleOptions.length,
          ...normalizedValues
        );
        this.scheduleOptions.splice(
          0,
          this.scheduleOptions.length,
          ...normalizedValues
        );
        this.applyScheduleOptionsIntersection();
        this.syncScheduleAction();
      } catch (e) {
        this.allScheduleOptions.splice(0, this.allScheduleOptions.length);
        this.scheduleOptions.splice(0, this.scheduleOptions.length);
        this.syncScheduleAction();
      }
    },
    selectionLabel(selected, options) {
      if (!selected || !selected.length) return "";
      if (selected.length === options.length) {
        return "All (" + options.length + ")";
      }
      return selected.length + " of " + options.length + " selected";
    },
    async loadScheduleFilterOptions(useDayCampClasses) {
      const isDC = !!useDayCampClasses;
      try {
        const branch = this.getSelectedBranch();
        let url = "api/studentStop/GetScheduleFilterOptions";
        const params = [];
        if (branch) params.push("branch=" + encodeURIComponent(branch));
        if (isDC) params.push("useDayCampClasses=true");
        if (params.length) url += "?" + params.join("&");
        const data = await this.$http.get(url);
        const list = Array.isArray(data) ? data : [];
        const target = isDC ? this.scheduleFilterOptionsDC : this.scheduleFilterOptions;
        target.splice(0, target.length, ...list);
      } catch (e) {
        const target = isDC ? this.scheduleFilterOptionsDC : this.scheduleFilterOptions;
        target.splice(0, target.length);
      }
    },
    getRowAccountId(row) {
      if (!row) return "";

      const accountId = row.familyID || "";

      return accountId ? String(accountId).trim() : "";
    },
    toAccount(row) {
      if (this.isBranchUser()) return;

      const accountId = this.getRowAccountId(row);
      if (!accountId) return;

      this.$router.push({
        name: "Account View",
        params: {
          id: accountId,
        },
        query: {
          goToGeoSection: true,
        },
      });
    },
    exportData() {
      const total = this.$refs.grid.getTotalCount();
      const chunkSize = 10000;

      this.exportToken = this.$store.state.authentication.token;
      this.exportBaseUrl = this.$refs.grid.buildExportUrl();
      this.exportFetched = 0;
      this.exportTotal = total || 0;
      this.exportCurrentChunk = 0;
      this.exportTotalChunks = total ? Math.ceil(total / chunkSize) : 0;
      this.exportCancelled = false;
      this.exportDone = false;
      this.exportRunning = false;
      this.exportBatchLoading = false;
      this.exportReadyRecords = null;
      this.exportAbortController = null;
      this.exportDialog = true;
    },
    openReportDialog() {
      // Setting reportSchedule triggers the watcher which handles
      // resetting fields and auto-selecting single-option values.
      this.reportSchedule = this.selectedSchedule || "";
      this.reportDialog = true;
      this.$nextTick(() => {
        if (this.$refs.reportSchedule) {
          this.$refs.reportSchedule.resetValidation();
        }
      });
    },
    closeReportDialog() {
      this.reportDialog = false;
      this.reportSchedule = "";
      this.reportBuildings = [];
      this.reportBranches = [];
      this.reportClasses = [];
      this.reportSections = [];
      this.reportPaperSize = "A4";
      this.reportUseDayCampClasses = false;
    },
    async downloadStudentStopsReport() {
      if (!this.$refs.reportSchedule.validate(true)) {
        return;
      }

      try {
        this.downloadingReport = true;
        const apiUrl = "api/studentStop/GenerateStudentStopsReport";

        const payload = {
          Schedule: this.reportSchedule,
          Buildings:
            this.reportBuildings && this.reportBuildings.length
              ? this.reportBuildings
              : null,
          Branches:
            !this.reportUseDayCampClasses && this.reportBranches && this.reportBranches.length
              ? this.reportBranches
              : null,
          Classes:
            this.reportClasses && this.reportClasses.length
              ? this.reportClasses
              : null,
          Sections:
            this.reportSections && this.reportSections.length
              ? this.reportSections
              : null,
          PaperSize: this.reportPaperSize || "A4",
          UseDayCampClasses: !!this.reportUseDayCampClasses,
        };

        const response = await this.$http.post(apiUrl, payload, {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        });

        const blob = new Blob([response], { type: "application/pdf" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        const scheduleName = String(this.reportSchedule).replace(
          /[^a-z0-9_-]/gi,
          "_"
        );
        link.download = "Bus_Schedule_Report_" + scheduleName + ".pdf";
        link.click();
        window.URL.revokeObjectURL(link.href);

        this.$notify({
          group: "notification",
          text: "Geo Student Stops report downloaded successfully",
          type: "success",
        });
        this.closeReportDialog();
      } catch (error) {
        console.error("Error downloading report:", error);
      } finally {
        this.downloadingReport = false;
      }
    },
    async startExport() {
      const chunkSize = 10000;
      const maxRetries = 3;
      const retryDelayMs = 800;
      const batchDelayMs = 150;
      const baseUrl = this.exportBaseUrl;
      const token = this.exportToken;
      const separator = baseUrl.includes("?") ? "&" : "?";

      this.exportRunning = true;
      this.exportCancelled = false;
      this.exportDone = false;
      this.exportFetched = 0;
      this.exportCurrentChunk = 0;
      this.exportReadyRecords = null;
      this.exportAbortController = new AbortController();

      this.exportBatchLoading = true;
      this.exportCurrentChunk = 1;

      let skip = 0;
      let allRecords = [];
      let hasMore = true;

      try {
        while (hasMore) {
          if (this.exportCancelled) break;

          const url =
            baseUrl + separator + "$top=" + chunkSize + "&$skip=" + skip;

          let response = null;
          let lastError = null;

          for (var attempt = 1; attempt <= maxRetries; attempt++) {
            if (this.exportCancelled) break;
            try {
              response = await fetch(url, {
                headers: { Authorization: token },
                signal: this.exportAbortController.signal,
              });
              if (response.ok) {
                lastError = null;
                break;
              }
              lastError = new Error("HTTP " + response.status);
            } catch (fetchErr) {
              lastError = fetchErr;
              if (this.exportCancelled) break;
              if (attempt < maxRetries) {
                await new Promise(function(resolve) {
                  setTimeout(resolve, retryDelayMs * attempt);
                });
              }
            }
          }

          if (this.exportCancelled) break;
          if (lastError) throw lastError;

          this.exportBatchLoading = false;

          const data = await response.json();
          const records = data.value || [];

          allRecords = allRecords.concat(records);
          this.exportFetched = allRecords.length;

          if (!this.exportTotal && data["@odata.count"]) {
            this.exportTotal = data["@odata.count"];
            this.exportTotalChunks = Math.ceil(this.exportTotal / chunkSize);
          }

          skip += chunkSize;
          hasMore = records.length === chunkSize;

          if (hasMore && !this.exportCancelled) {
            this.exportCurrentChunk = Math.floor(skip / chunkSize) + 1;
            this.exportBatchLoading = true;
            await new Promise(function(resolve) {
              setTimeout(resolve, batchDelayMs);
            });
          }
        }

        if (!this.exportCancelled) {
          this.exportBatchLoading = false;
          this.exportRunning = false;
          this.exportReadyRecords = allRecords;
          this.exportDone = true;
        }
      } catch (e) {
        this.exportBatchLoading = false;
        this.exportRunning = false;

        if (this.exportCancelled) return;

        this.exportDialog = false;
        this.$notify({
          group: "notification",
          text:
            "Export failed: " + (e && e.message ? e.message : "Unknown error"),
          type: "error",
        });
      }
    },
    cancelExport() {
      this.exportCancelled = true;
      this.exportRunning = false;
      this.exportBatchLoading = false;
      if (this.exportAbortController) {
        this.exportAbortController.abort();
      }
    },
    triggerDownload() {
      this.downloadCSV(this.exportReadyRecords);
      this.exportDialog = false;
      this.exportReadyRecords = null;
    },
    closeExportDialog() {
      this.exportDialog = false;
      this.exportReadyRecords = null;
    },
    downloadCSV(records) {
      if (!records || !records.length) {
        this.$notify({
          group: "notification",
          text: "No records to export",
          type: "warning",
        });
        return;
      }

      const exportColumns = this.columns.filter(function(c) {
        return c.label;
      });

      const escapeCSV = function(value) {
        if (value === null || value === undefined) return "";
        const str = String(value);
        if (
          str.indexOf(",") !== -1 ||
          str.indexOf('"') !== -1 ||
          str.indexOf("\n") !== -1
        ) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      };

      const header = exportColumns
        .map(function(c) {
          return escapeCSV(c.label);
        })
        .join(",");

      const rows = records.map(function(record) {
        return exportColumns
          .map(function(col) {
            return escapeCSV(record[col.field]);
          })
          .join(",");
      });

      const csv = [header].concat(rows).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", "GeoStudentStops.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    },
  },
};
</script>

<style scoped>
.export-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.export-dialog-header {
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
}

.export-dialog-title {
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.export-status-row {
  display: flex;
  align-items: center;
}

.export-status-text {
  font-size: 14px;
  color: #555;
}

.export-idle-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 4px;
}

.export-idle-icon-wrap {
  background: #c8e6c9;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.export-idle-title {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.export-idle-subtitle {
  font-size: 13px;
  text-align: center;
  line-height: 1.5;
  max-width: 320px;
}

.export-progress-wrap {
  position: relative;
}

.export-shimmer-bar {
  margin-top: 3px !important;
  border-radius: 4px;
  opacity: 0.85;
}

.export-counts-row {
  display: flex;
  align-items: baseline;
}

.export-count-fetched {
  font-size: 20px;
  font-weight: 600;
  color: #2e7d32;
}

.export-count-sep {
  font-size: 16px;
  color: #aaa;
  margin: 0 2px;
}

.export-count-total {
  font-size: 14px;
  color: #777;
}

.export-percent-badge {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #4caf50;
  border-radius: 12px;
  padding: 2px 10px;
}

.export-chunk-info {
  font-size: 12px;
  color: #aaa;
  min-height: 20px;
}

.export-batch-loading-text {
  color: #558b2f;
  font-weight: 500;
}

.export-done-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0 4px;
}

.export-done-check-wrap {
  background: #e8f5e9;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.export-done-subtitle {
  font-size: 13px;
  text-align: center;
}

.export-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 11px 28px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.4px;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(76, 175, 80, 0.4);
  transition: box-shadow 0.2s, transform 0.15s, filter 0.15s;
}

.export-action-btn:hover {
  filter: brightness(1.07);
  box-shadow: 0 5px 14px rgba(76, 175, 80, 0.5);
  transform: translateY(-1px);
}

.export-action-btn:active {
  filter: brightness(0.95);
  transform: translateY(0);
  box-shadow: 0 2px 5px rgba(76, 175, 80, 0.35);
}

.export-action-btn-inner {
  display: flex;
  align-items: center;
}

.export-cancelled-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0 8px;
}

.export-final-text {
  font-size: 15px;
  font-weight: 500;
  text-align: center;
}

.pulse-icon {
  animation: pulse 1.4s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
}

.export-spin-icon {
  animation: spin 1s linear infinite;
  display: inline-block;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.report-dialog-card {
  border-radius: 10px;
  overflow: hidden;
}

.report-dialog-header {
  background: #4caf50;
  padding: 18px 24px;
  display: flex;
  align-items: center;
}

.report-dialog-title {
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.report-dialog-subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 12px;
  margin-top: 2px;
}

.report-section-label {
  font-size: 11px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  display: flex;
  align-items: center;
}

.report-card-body {
  max-height: 70vh;
  overflow-y: auto;
}

.report-filter-hint {
  font-size: 12px;
  color: #757575;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
}

.report-select-summary {
  font-size: 13px;
  color: #2e7d32;
  font-weight: 500;
  padding: 2px 0;
}

.report-optional-badge {
  margin-left: 8px;
  font-size: 10px;
  font-weight: 500;
  color: #fff;
  background: #9e9e9e;
  border-radius: 10px;
  padding: 1px 7px;
  text-transform: lowercase;
  letter-spacing: 0;
}

.report-branch-chip {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 13px;
  color: #1565c0;
  display: flex;
  align-items: center;
}
</style>
